import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar, Clock, Users, CreditCard, CheckCircle, AlertCircle,
  X, MapPin, User, Zap, ShoppingBag, XCircle, ChevronLeft, ChevronRight, SlidersHorizontal
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MAAND_NAMEN = ["Januari", "Februari", "Maart", "April", "Mei", "Juni", "Juli", "Augustus", "September", "Oktober", "November", "December"];
const DAG_NAMEN_KORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function formatDate(datum) {
  if (!datum) return "";
  const d = new Date(datum);
  return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
}

function getLessDatumStr(datum) {
  if (!datum) return "";
  return new Date(datum).toISOString().split("T")[0];
}

// ── Mini kalender component ───────────────────────────────────────────────────
function MiniKalender({ lessen, geselecteerdeDatum, onSelect }) {
  const vandaagStr = new Date().toISOString().split("T")[0];
  const startJaar  = new Date().getFullYear();
  const startMaand = new Date().getMonth();

  const [viewJaar,  setViewJaar]  = useState(startJaar);
  const [viewMaand, setViewMaand] = useState(startMaand);

  // Verzamel datums die lessen hebben
  const datumMetLessen = useMemo(() => {
    const s = new Set();
    lessen.forEach(l => { if (l.datum) s.add(getLessDatumStr(l.datum)); });
    return s;
  }, [lessen]);

  const vorigemMaand = () => {
    if (viewMaand === 0) { setViewMaand(11); setViewJaar(y => y - 1); }
    else setViewMaand(m => m - 1);
  };
  const volgendeMaand = () => {
    if (viewMaand === 11) { setViewMaand(0); setViewJaar(y => y + 1); }
    else setViewMaand(m => m + 1);
  };

  const aantalDagen   = new Date(viewJaar, viewMaand + 1, 0).getDate();
  const eersteWeekdag = (new Date(viewJaar, viewMaand, 1).getDay() + 6) % 7; // 0=Ma, 6=Zo

  const formatDatumStr = (dag) => {
    const m = String(viewMaand + 1).padStart(2, "0");
    const d = String(dag).padStart(2, "0");
    return `${viewJaar}-${m}-${d}`;
  };

  return (
    <div className="bg-[#1a1a1a] border border-white/8 rounded-2xl p-3 mb-4">
      {/* Maand navigatie */}
      <div className="flex items-center justify-between mb-3">
        <button onClick={vorigemMaand} className="p-1.5 text-zinc-500 hover:text-white transition-colors rounded-lg hover:bg-white/5">
          <ChevronLeft size={15} />
        </button>
        <span className="text-white text-sm font-bold">{MAAND_NAMEN[viewMaand]} {viewJaar}</span>
        <button onClick={volgendeMaand} className="p-1.5 text-zinc-500 hover:text-white transition-colors rounded-lg hover:bg-white/5">
          <ChevronRight size={15} />
        </button>
      </div>

      {/* Dag-headers (Ma–Zo) */}
      <div className="grid grid-cols-7 mb-1">
        {DAG_NAMEN_KORT.map(d => (
          <div key={d} className="text-center text-zinc-600 text-[10px] font-semibold py-0.5">{d}</div>
        ))}
      </div>

      {/* Dag-cellen */}
      <div className="grid grid-cols-7 gap-0.5">
        {/* Lege cellen voor opvulling */}
        {Array.from({ length: eersteWeekdag }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {/* Dag-knoppen */}
        {Array.from({ length: aantalDagen }, (_, i) => i + 1).map(dag => {
          const datumStr    = formatDatumStr(dag);
          const heeftLes    = datumMetLessen.has(datumStr);
          const geselecteerd = geselecteerdeDatum === datumStr;
          const isVandaag   = datumStr === vandaagStr;

          return (
            <button
              key={dag}
              onClick={() => heeftLes && onSelect(geselecteerd ? null : datumStr)}
              data-testid={`kalender-dag-${datumStr}`}
              className={`relative flex items-center justify-center h-8 rounded-lg text-xs font-bold transition-all ${
                geselecteerd
                  ? "text-black"
                  : isVandaag && heeftLes
                  ? "text-[#C9A84C] border border-[#C9A84C]/40 hover:bg-[#C9A84C]/10"
                  : heeftLes
                  ? "text-white hover:bg-white/10"
                  : "text-zinc-700 cursor-default"
              }`}
              style={geselecteerd ? { background: "#C9A84C" } : {}}
            >
              {dag}
              {heeftLes && !geselecteerd && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#C9A84C]" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-3 mt-3 pt-2.5 border-t border-white/5">
        <span className="flex items-center gap-1.5 text-zinc-600 text-[10px]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] inline-block" /> Les beschikbaar
        </span>
        {geselecteerdeDatum && (
          <button onClick={() => onSelect(null)} className="ml-auto text-[10px] text-zinc-500 hover:text-white flex items-center gap-1">
            <X size={9} /> Wissen
          </button>
        )}
      </div>
    </div>
  );
}

function CreditKoopModal({ pakketten, token, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const koop = async (pakket) => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/schedule/credits/kopen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pakket_id: pakket.id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Betaling aanmaken mislukt");
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <div>
            <h3 className="text-white font-bold">Credits kopen</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Kies een pakket</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {pakketten.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-4">Geen pakketten beschikbaar</p>
          ) : (
            pakketten.map(p => (
              <button
                key={p.id}
                onClick={() => koop(p)}
                disabled={loading}
                data-testid={`koop-pakket-btn-${p.id}`}
                className="w-full flex items-center justify-between p-4 rounded-xl border border-white/10 hover:border-[#C9A84C]/40 transition-all group disabled:opacity-40"
                style={{ background: "#1a1a1a" }}
              >
                <div className="text-left">
                  <p className="text-white font-bold text-sm group-hover:text-[#C9A84C] transition-colors">{p.naam}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{p.credits} credits{p.beschrijving && ` · ${p.beschrijving}`}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-[#C9A84C] font-black text-sm">€{Number(p.prijs).toFixed(2).replace(".", ",")}</p>
                  <p className="text-zinc-600 text-xs">{(p.prijs / p.credits).toFixed(2).replace(".", ",")} per credit</p>
                </div>
              </button>
            ))
          )}
          {error && (
            <div className="flex items-start gap-2 bg-red-950/50 border border-red-500/30 rounded-xl p-3">
              <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SporterRoosterSection({ user }) {
  const [lessen, setLessen] = useState([]);
  const [mijnBoekingen, setMijnBoekingen] = useState([]);
  const [creditsSaldo, setCreditsSaldo] = useState(0);
  const [pakketten, setPakketten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKoopModal, setShowKoopModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("rooster");

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterDatum,   setFilterDatum]   = useState(null); // "YYYY-MM-DD" of null
  const [filterType,    setFilterType]    = useState(null); // lesnaam of null
  const [filterTrainer, setFilterTrainer] = useState(null); // instructeur_naam of null

  // Unieke trainingstypes (lesnamen), gesorteerd
  const beschikbareTypes = useMemo(() => {
    return [...new Set(lessen.map(l => l.naam).filter(Boolean))].sort();
  }, [lessen]);

  // Unieke trainers, gesorteerd
  const beschikbareTrainers = useMemo(() => {
    return [...new Set(lessen.map(l => l.instructeur_naam).filter(Boolean))].sort();
  }, [lessen]);

  // Gefilterde lessen
  const gefilterdeL = useMemo(() => {
    return lessen.filter(l => {
      const datumOk   = filterDatum   === null || getLessDatumStr(l.datum) === filterDatum;
      const typeOk    = filterType    === null || l.naam === filterType;
      const trainerOk = filterTrainer === null || l.instructeur_naam === filterTrainer;
      return datumOk && typeOk && trainerOk;
    });
  }, [lessen, filterDatum, filterType, filterTrainer]);

  const actieveFilters = (filterDatum !== null ? 1 : 0) + (filterType !== null ? 1 : 0) + (filterTrainer !== null ? 1 : 0);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/schedule/rooster`, { headers }),
        fetch(`${API}/api/schedule/mijn-boekingen`, { headers }),
        fetch(`${API}/api/schedule/creditpakketten`, { headers }),
      ]);
      if (r1.ok) {
        const d = await r1.json();
        setLessen(d.lessen || []);
        setCreditsSaldo(d.credits_saldo || 0);
      }
      if (r2.ok) setMijnBoekingen((await r2.json()).boekingen || []);
      if (r3.ok) setPakketten((await r3.json()).pakketten || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [user?.token]);

  useEffect(() => { load(); }, [load]);

  const handleBoek = async (les) => {
    if (les.credits_kosten > creditsSaldo) {
      showToast(`Onvoldoende credits. Je hebt ${creditsSaldo} credits, les kost ${les.credits_kosten}.`, "error");
      return;
    }
    try {
      const r = await fetch(`${API}/api/schedule/boekingen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({ les_id: les.id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Boeken mislukt");
      if (d.wachtlijst) {
        showToast("Op de wachtlijst geplaatst! Je wordt automatisch bevorderd als er plek vrijkomt.");
      } else {
        showToast(`Geboekt! ${d.credits_betaald > 0 ? `${d.credits_betaald} credits afgeschreven.` : ""}`);
      }
      load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleAnnuleer = async (boekingId) => {
    if (!window.confirm("Boeking annuleren?")) return;
    try {
      const r = await fetch(`${API}/api/schedule/boekingen/${boekingId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${user.token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Annuleren mislukt");
      if (d.terugstorting) {
        showToast(`Boeking geannuleerd · ${d.credits_teruggestort} credits teruggestort`);
      } else {
        showToast("Boeking geannuleerd (geen terugstorting binnen 24u van aanvang)");
      }
      load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const hasPakketten = pakketten.length > 0;

  return (
    <div className="account-card mb-6" data-testid="sporter-rooster-section">
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Sportschool</p>
          <h2 className="text-white font-bold text-xl">Rooster & Credits</h2>
          <p className="text-zinc-500 text-sm mt-0.5">Boek lessen en beheer je credits</p>
        </div>
        {/* Credit balance */}
        <div className="text-right shrink-0 ml-4">
          <p className="text-[#C9A84C] text-2xl font-black tabular-nums">{creditsSaldo}</p>
          <p className="text-zinc-600 text-xs">credits</p>
          {hasPakketten && (
            <button
              onClick={() => setShowKoopModal(true)}
              data-testid="koop-credits-btn"
              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-black text-xs font-bold transition-opacity hover:opacity-90"
              style={{ background: "#C9A84C" }}>
              <ShoppingBag size={11} /> Kopen
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-[#1a1a1a] border border-white/5 rounded-xl p-1 mb-5">
        {[{ key: "rooster", label: "Rooster" }, { key: "boekingen", label: `Mijn boekingen${mijnBoekingen.length > 0 ? ` (${mijnBoekingen.length})` : ""}` }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === t.key ? "text-black" : "text-zinc-400 hover:text-white"}`}
            style={activeTab === t.key ? { background: "#C9A84C" } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-20 bg-[#1a1a1a] rounded-xl animate-pulse" />)}
        </div>
      ) : activeTab === "rooster" ? (
        <>
          {/* ── Kalender + Filterbalken ───────────────────────────────── */}
          {lessen.length > 0 && (
            <div className="mb-4 space-y-2.5">
              {/* Datum-kalender */}
              <MiniKalender
                lessen={lessen}
                geselecteerdeDatum={filterDatum}
                onSelect={setFilterDatum}
              />

              {/* Type-filter */}
              {beschikbareTypes.length > 1 && (
                <div>
                  <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-semibold mb-1.5 flex items-center gap-1.5">
                    <SlidersHorizontal size={9} /> Training
                  </p>
                  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    <button
                      onClick={() => setFilterType(null)}
                      data-testid="type-filter-alle"
                      className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                        filterType === null
                          ? "text-black border-transparent"
                          : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                      }`}
                      style={filterType === null ? { background: "#C9A84C" } : {}}>
                      Alle
                    </button>
                    {beschikbareTypes.map(type => (
                      <button
                        key={type}
                        onClick={() => setFilterType(filterType === type ? null : type)}
                        data-testid={`type-filter-${type.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap ${
                          filterType === type
                            ? "text-black border-transparent"
                            : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                        }`}
                        style={filterType === type ? { background: "#C9A84C" } : {}}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Trainer-filter */}
              {beschikbareTrainers.length > 1 && (
                <div>
                  <p className="text-zinc-600 text-[10px] uppercase tracking-widest font-semibold mb-1.5 flex items-center gap-1.5">
                    <User size={9} /> Trainer
                  </p>
                  <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    <button
                      onClick={() => setFilterTrainer(null)}
                      data-testid="trainer-filter-alle"
                      className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                        filterTrainer === null
                          ? "text-black border-transparent"
                          : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                      }`}
                      style={filterTrainer === null ? { background: "#C9A84C" } : {}}>
                      Alle
                    </button>
                    {beschikbareTrainers.map(trainer => (
                      <button
                        key={trainer}
                        onClick={() => setFilterTrainer(filterTrainer === trainer ? null : trainer)}
                        data-testid={`trainer-filter-${trainer.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap ${
                          filterTrainer === trainer
                            ? "text-black border-transparent"
                            : "bg-[#1a1a1a] border-white/10 text-zinc-400 hover:text-white"
                        }`}
                        style={filterTrainer === trainer ? { background: "#C9A84C" } : {}}>
                        {trainer}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reset alle filters */}
              {actieveFilters > 0 && (
                <button
                  onClick={() => { setFilterDatum(null); setFilterType(null); setFilterTrainer(null); }}
                  data-testid="reset-filters-btn"
                  className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition-colors">
                  <X size={10} /> Filters wissen ({actieveFilters})
                </button>
              )}
            </div>
          )}

          {/* ── Lessenlijst ───────────────────────────────────────────── */}
          {gefilterdeL.length === 0 ? (
            <div className="text-center py-6">
              <Calendar size={28} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">
                {actieveFilters > 0 ? "Geen lessen gevonden voor deze filter" : "Geen aankomende lessen"}
              </p>
              {actieveFilters > 0 && (
                <button
                  onClick={() => { setFilterDatum(null); setFilterType(null); setFilterTrainer(null); }}
                  className="text-[#C9A84C] text-xs mt-2 hover:underline">
                  Filters wissen
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {gefilterdeL.map(les => {
              const geboekt = les.mijn_boeking;
              const isVol = les.is_vol && !geboekt;
              const needsCredits = les.credits_kosten > 0;

              return (
                <div key={les.id}
                  className={`rounded-xl border overflow-hidden transition-all ${geboekt ? "border-[#C9A84C]/30 bg-[#C9A84C]/5" : "border-white/8 bg-[#1a1a1a]"}`}
                  data-testid={`les-card-${les.id}`}>
                  <div className="p-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="text-white font-bold text-sm">{les.naam}</p>
                          {geboekt && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${geboekt.status === "wachtlijst" ? "bg-yellow-500/15 text-yellow-400" : "bg-green-500/15 text-green-400"}`}>
                              {geboekt.status === "wachtlijst" ? "Wachtlijst" : "Geboekt"}
                            </span>
                          )}
                          {les.credits_kosten === 0 ? (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 font-semibold">Gratis</span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C9A84C]/10 text-[#C9A84C] font-bold">{les.credits_kosten} credits</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-zinc-500 text-xs flex items-center gap-1">
                            <Calendar size={10} /> {formatDate(les.datum)}
                          </span>
                          <span className="text-zinc-500 text-xs flex items-center gap-1">
                            <Clock size={10} /> {les.start_tijd} ({les.duur_minuten} min)
                          </span>
                          {les.locatie && (
                            <span className="text-zinc-500 text-xs flex items-center gap-1">
                              <MapPin size={10} /> {les.locatie}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-zinc-500 text-xs">{les.boekingen_count}/{les.max_deelnemers}</p>
                        {les.wachtlijst_count > 0 && (
                          <p className="text-yellow-400 text-[10px]">+{les.wachtlijst_count} wl</p>
                        )}
                      </div>
                    </div>

                    {/* Action button */}
                    <div className="mt-3">
                      {geboekt ? (
                        <button
                          onClick={() => handleAnnuleer(geboekt.id)}
                          data-testid={`annuleer-boeking-btn-${les.id}`}
                          className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1 transition-colors">
                          <XCircle size={11} /> Annuleren
                        </button>
                      ) : needsCredits && creditsSaldo < les.credits_kosten ? (
                        <div className="flex items-center gap-2">
                          <p className="text-zinc-600 text-xs">Onvoldoende credits</p>
                          {hasPakketten && (
                            <button onClick={() => setShowKoopModal(true)}
                              className="text-xs text-[#C9A84C] font-semibold hover:underline">
                              Credits kopen →
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => handleBoek(les)}
                          data-testid={`boek-les-btn-${les.id}`}
                          disabled={isVol && les.wachtlijst_count >= 10}
                          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-black text-xs font-bold transition-opacity hover:opacity-90 disabled:opacity-40"
                          style={{ background: "#C9A84C" }}>
                          <Zap size={11} />
                          {isVol ? "Op wachtlijst" : "Boeken"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </>
      ) : (
        mijnBoekingen.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle size={28} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">Geen aankomende boekingen</p>
          </div>
        ) : (
          <div className="space-y-3">
            {mijnBoekingen.map(b => (
              <div key={b.id} className="bg-[#1a1a1a] border border-white/8 rounded-xl p-3.5"
                data-testid={`mijn-boeking-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-semibold text-sm">{b.les?.naam || "Les"}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${b.status === "wachtlijst" ? "bg-yellow-500/15 text-yellow-400" : "bg-green-500/15 text-green-400"}`}>
                        {b.status === "wachtlijst" ? "Wachtlijst" : "Bevestigd"}
                      </span>
                    </div>
                    <p className="text-zinc-500 text-xs">
                      {formatDate(b.les?.datum)} · {b.les?.start_tijd}
                      {b.les?.locatie && ` · ${b.les.locatie}`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAnnuleer(b.id)}
                    data-testid={`annuleer-boeking-overzicht-btn-${b.id}`}
                    className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-50 flex items-start gap-3 px-4 py-3 rounded-xl text-sm font-semibold shadow-2xl ${toast.type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
          {toast.type === "success" ? <CheckCircle size={16} className="text-[#C9A84C] shrink-0 mt-0.5" /> : <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Koop credits modal */}
      {showKoopModal && (
        <CreditKoopModal
          pakketten={pakketten}
          token={user.token}
          onClose={() => setShowKoopModal(false)}
          onSuccess={() => { setShowKoopModal(false); load(); }}
        />
      )}
    </div>
  );
}
